import React, { FC, memo, useState } from 'react';
import { Typography, Space, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import Icon, { IconClass } from '../Icon';
import { WaybillTrackLogResponse } from '@/services/type';
import copy from 'copy-to-clipboard';
import classNames from 'classnames';

const { Text } = Typography;

interface Props {
  id: string;
  data?: WaybillTrackLogResponse;
}

const Index: FC<Props> = ({ id, data: detail }) => {
  const [showDetail, setShowDetail] = useState(true);

  return (
    <div className='absolute top-0 left-0 h-full z-10'>
      <div>
        <div
          className={classNames(
            'absolute top-0 bottom-0 m-auto z-10 w-5 h-10 cursor-pointer bg-white rounded-tr-2xl rounded-br-2xl shadow border border-gray-200 flex items-center justify-center transition-all',
            {
              'left-0': !showDetail,
              'left-[322px]': showDetail,
            },
          )}
          onClick={() => setShowDetail((prev) => !prev)}
        >
          <span className='flex items-center justify-center w-8 h-8'>
            <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
              <polygon points={showDetail ? '15,8 15,16 9,12' : '9,8 9,16 15,12'} fill='#222' />
            </svg>
          </span>
        </div>
      </div>
      {showDetail && (
        <div className='w-[322px] h-full bg-gray-50 border-r border-gray-200 overflow-y-auto flex flex-col'>
          <div className='p-4 border-b bg-white border-gray-100'>
            <Space className='w-full gap-2'>
              <Text className='text-base'>订单号：{id}</Text>
              <CopyOutlined
                onClick={() => {
                  copy(id ?? '');
                  message.success('复制成功');
                }}
                className='text-gray-400 cursor-pointer'
              />
            </Space>
          </div>

          <div className='p-4 relative bg-white'>
            <div className='absolute left-[27px] top-[30px] bottom-[30px] border-l border-dashed border-gray-300' />

            <div className='flex gap-3 mb-6 relative z-10 w-full'>
              <div className='w-6 h-6 bg-white flex items-center justify-center'>
                <Icon type={IconClass.SEND_ADDRESS} symbol className='text-2xl' />
              </div>
              <div className='flex items-center justify-between w-full'>
                <div className='text-gray-500 text-xs'>取货时间</div>
                <div className='text-sm font-medium'>
                  {detail?.orderDetail?.orderAddress?.sendReadyDate}{' '}
                  {detail?.orderDetail?.orderAddress?.sendReadyTime as string}
                </div>
              </div>
            </div>

            <div className='flex gap-3 relative z-10'>
              <div className='w-6 h-6 bg-white flex items-center justify-center'>
                <Icon type={IconClass.RECEIVE_ADDRESS} symbol className='text-2xl' />
              </div>
              <div className='flex items-center justify-between w-full'>
                <div className='text-gray-500 text-xs'>收货时间</div>
                <div className='text-sm font-medium'>
                  {detail?.orderDetail?.orderAddress?.receiveReadyDate}{' '}
                  {detail?.orderDetail?.orderAddress?.receiveReadyTime as string}
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className='flex items-center gap-1 px-4 py-3'>
              <div className='w-1 h-3 bg-blue-600' />
              <div className='font-bold text-sm text-gray-900'>订单详情</div>
            </div>

            <div className='p-4 bg-white'>
              {detail?.orderDetail?.orderAddress?.sendCompany && (
                <div className='flex justify-between mb-2'>
                  <div className='text-gray-400 w-[70px]'>公司名称</div>
                  <div className='flex-1 text-right text-gray-900'>
                    {detail?.orderDetail?.orderAddress?.sendCompany}
                  </div>
                </div>
              )}
              {!detail?.orderDetail?.orderAddress?.sendCompany && detail?.orderDetail?.orderAddress?.sendContact && (
                <div className='flex justify-between mb-2'>
                  <div className='text-gray-400 w-[70px]'>联系人</div>
                  <div className='flex-1 text-right text-gray-900'>
                    {detail?.orderDetail?.orderAddress?.sendContact}
                  </div>
                </div>
              )}
              <div className='flex justify-between mb-2'>
                <div className='text-gray-400 w-[70px]'>具体地址</div>
                <div className='flex-1 text-right text-gray-900'>{detail?.orderDetail?.orderAddress?.sendAddress}</div>
              </div>
              <div className='flex justify-between mb-2'>
                <div className='text-gray-400 w-[70px]'>取货时间</div>
                <div className='flex-1 text-right text-gray-900'>
                  <Space size={4}>
                    <div className='text-gray-900'>当地时间</div>
                    <div className='text-gray-900'>
                      {detail?.orderDetail?.orderAddress?.sendOpentime as string}-
                      {detail?.orderDetail?.orderAddress?.sendClosetime as string}
                    </div>
                  </Space>
                </div>
              </div>
              <div className='flex justify-between mb-2'>
                <div className='text-gray-400 w-[70px]'>联系邮箱</div>
                <div className='flex-1 text-right text-gray-900 break-all'>
                  {detail?.orderDetail?.orderAddress?.sendContactEmail}
                </div>
              </div>
              <div className='flex justify-between'>
                <div className='text-gray-400 w-[70px]'>联系电话</div>
                <div className='flex-1 text-right text-gray-900'>
                  {detail?.orderDetail?.orderAddress?.sendContactPhone}
                </div>
              </div>
            </div>

            <div className='h-2 bg-gray-50' />

            <div className='pt-4 p-4 bg-white'>
              {detail?.orderDetail?.orderAddress?.receiveCompany && (
                <div className='flex justify-between mb-2'>
                  <div className='text-gray-400 w-[70px]'>公司名称</div>
                  <div className='flex-1 text-right text-gray-900'>
                    {detail?.orderDetail?.orderAddress?.receiveCompany}
                  </div>
                </div>
              )}
              {!detail?.orderDetail?.orderAddress?.receiveCompany &&
                detail?.orderDetail?.orderAddress?.receiveContact && (
                  <div className='flex justify-between mb-2'>
                    <div className='text-gray-400 w-[70px]'>联系人</div>
                    <div className='flex-1 text-right text-gray-900'>
                      {detail?.orderDetail?.orderAddress?.receiveContact}
                    </div>
                  </div>
                )}
              <div className='flex justify-between mb-2'>
                <div className='text-gray-400 w-[70px]'>具体地址</div>
                <div className='flex-1 text-right text-gray-900'>
                  {detail?.orderDetail?.orderAddress?.receiveAddress}
                </div>
              </div>
              <div className='flex justify-between mb-2'>
                <div className='text-gray-400 w-[70px]'>收货时间</div>
                <div className='flex-1 text-right text-gray-900'>
                  <Space size={4}>
                    <div className='text-gray-900'>当地时间</div>
                    <div className='text-gray-900'>
                      {detail?.orderDetail?.orderAddress?.receiveOpentime as string}-
                      {detail?.orderDetail?.orderAddress?.receiveClosetime as string}
                    </div>
                  </Space>
                </div>
              </div>
              <div className='flex justify-between mb-2'>
                <div className='text-gray-400 w-[70px]'>联系邮箱</div>
                <div className='flex-1 text-right text-gray-900 break-all'>
                  {detail?.orderDetail?.orderAddress?.receiveContactEmail}
                </div>
              </div>
              <div className='flex justify-between'>
                <div className='text-gray-400 w-[70px]'>联系电话</div>
                <div className='flex-1 text-right text-gray-900'>
                  {detail?.orderDetail?.orderAddress?.receiveContactPhone}
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className='flex items-center gap-1 px-4 py-3'>
              <div className='w-1 h-3 bg-blue-600' />
              <div className='font-bold text-sm text-gray-900'>货物信息</div>
            </div>

            {detail?.orderDetail?.orderCargoList?.map((item, index) => (
              <div key={index} className='space-y-2 p-4 bg-white border-b border-gray-100 last:border-none'>
                <div className='flex justify-between'>
                  <div className='text-gray-400'>货物名称</div>
                  <div className='text-gray-900'>{item?.cargoInfo}</div>
                </div>
                <div className='flex justify-between'>
                  <div className='text-gray-400'>托盘数量</div>
                  <div className='text-gray-900'>{item?.quantity}</div>
                </div>
                <div className='flex justify-between'>
                  <div className='text-gray-400'>重量/每托</div>
                  <div className='text-gray-900'>{item?.weight}</div>
                </div>
                <div className='flex justify-between'>
                  <div className='text-gray-400'>长度/每托</div>
                  <div className='text-gray-900'>{item?.length}</div>
                </div>
                <div className='flex justify-between'>
                  <div className='text-gray-400'>高度/每托</div>
                  <div className='text-gray-900'>{item?.height}</div>
                </div>
                <div className='flex justify-between'>
                  <div className='text-gray-400'>宽度/每托</div>
                  <div className='text-gray-900'>{item?.width}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(Index);
